/*
  Script of sections/install-advisor.liquid, moved out of the section so browsers can cache it. OURS.

  Each advisor section prints its settings (product catalogue, montage prices, backend URL, section id) as
  <script type="application/json" data-install-advisor-config>. This file reads every one of them and starts a wizard
  for it. It runs again on shopify:section:load so the theme editor keeps working after a section re-renders; a wizard
  root that is already running carries data-advisor-ready and is skipped.
*/

(function () {
  'use strict';

  if (window.installAdvisorReady) return;
  window.installAdvisorReady = true;

  function setupAdvisor(config) {

    // ════════════════════════════════════════════════════════════════════════
    // 1. PRODUKT-KATALOG
    //    Wird automatisch aus den Produkt-Datensatz-Blöcken im Editor befüllt.
    //    Shopify-Preise sind in Cent gespeichert (z.B. 24000 = 240,00 €).
    //    Wir teilen durch 100, um echte Euro-Beträge zu bekommen.
    //
    //    WICHTIG: Die Schlüssel (z.B. 'apollo-2') müssen exakt mit dem
    //    Produkt-Handle-Feld im jeweiligen Produkt-Block übereinstimmen.
    // ════════════════════════════════════════════════════════════════════════
    const PRODUCT_CATALOG = config.catalog;

    // ════════════════════════════════════════════════════════════════════════
    // 2. PREIS-KONFIGURATION
    //    Hier werden alle Montage-Preise definiert. Produktpreise kommen
    //    automatisch aus Shopify (PRODUCT_CATALOG oben).
    //
    //    montage_tiers: Staffelung der Montagekosten nach Geräteanzahl.
    //    anschlusspauschale: Einmalige Gebühr pro installiertem Gerät.
    //
    //    Diese Werte sind auch als Sektions-Einstellungen im Editor verfügbar:
    //    section.settings.montage_tier1_price usw.
    // ════════════════════════════════════════════════════════════════════════
    const PRICE_CONFIG = {
      // Montage-Staffelung: wie viele Geräte → welcher Preis
      montage: {
        // 1 Gerät: Pauschalpreis (deckt Anfahrt und Einbau ab)
        1: { mode: 'flat',        rate: config.montage.tier1 },
        // 2 Geräte: Preis pro Gerät
        2: { mode: 'per-device',  rate: config.montage.tier2 },
        // 3 oder mehr Geräte: günstigerer Preis pro Gerät (Mengenrabatt)
        default: { mode: 'per-device', rate: config.montage.tier3 },
      },

      // Anschlusspauschale: wird pro installiertem Hauptgerät berechnet
      // (Kameras + Türklingeln + NVR, aber NICHT SD-Karten)
      anschlusspauschale: config.anschlusspauschale,

      // Handles der Kamera-Produkte (für Phase-2-Kalkulation)
      // Diese werden multipliziert mit der Anzahl der Phase-2-Bereiche
      cameraHandles:   ['apollo-2', 'iris-dome', 'oculus-pro'],
      doorbellHandles: ['prov1', 'cardea-3'],
      storageHandles:  ['chronos-nvr', 'sd-karte'],
    };

    // ════════════════════════════════════════════════════════════════════════
    // 3. FLOW RULES – "Wann erscheint welche Frage?"
    //    ────────────────────────────────────────────
    //    Stell dir das wie ein Entscheidungsbaum vor:
    //    Jede Frage hat eine "Wächter-Funktion", die schaut, ob die bisherigen
    //    Antworten die Bedingung erfüllen. Wenn ja → Frage erscheint.
    //    Wenn nein → Frage wird übersprungen.
    //
    //    Der Parameter "s" ist das State-Objekt mit allen bisherigen Antworten.
    //    Beispiel: s.q2_bereiche ist ein Array wie ['innen', 'aussen']
    //
    //    Um eine Frage IMMER zu zeigen: () => true
    //    Um eine Frage NIE zu zeigen: () => false
    //    Zum Überspringen einer Frage: einfach die Bedingung anpassen.
    // ════════════════════════════════════════════════════════════════════════
    const FLOW_RULES = {

      // ── Phase 1 ────────────────────────────────────────────────────────────

      // Q1: Immobilientyp – immer sichtbar
      'q1_immobilie': () => true,

      // Q2: Bereiche – immer sichtbar
      'q2_bereiche': () => true,

      // Q3: Steckdose – nur wenn Innen ODER Außen ausgewählt wurde
      // (Türklingel-Bereich braucht keine separate Stromanfrage)
      'q3_steckdose': (s) => _hasAny(s.q2_bereiche, ['innen', 'aussen']),

      // Q4: Klingeldrähte – nur wenn Eingangsbereich ausgewählt wurde
      'q4_klingeldraht': (s) => _has(s.q2_bereiche, 'eingangsbereich'),

      // Q5: Bereichsgröße für Außenbereich – nur wenn:
      //   1. Außen ausgewählt UND
      //   2. Steckdose vorhanden (weil batterie-Kamera ohnehin nur kurze Reichweite)
      'q5_groesse': (s) => _has(s.q2_bereiche, 'aussen') && s.q3_steckdose === 'ja',

      // Q6: Aufzeichnung speichern? – immer sichtbar
      'q6_aufzeichnung': () => true,

      // Q7: Speichertyp – nur wenn:
      //   1. Steckdose vorhanden (Netzwerkspeicher braucht Strom) UND
      //   2. Aufzeichnung gewünscht
      //   Hinweis: Wenn Q7 übersprungen wird und Q6=Ja → automatisch SD-Karte
      'q7_speicher': (s) => s.q3_steckdose === 'ja' && s.q6_aufzeichnung === 'ja',

      // Empfehlung – immer sichtbar (Ende Phase 1)
      'empfehlung': () => true,

      // ── Phase 2 (nur wenn "Einbau anfragen" geklickt wurde) ────────────────
      // Alle Phase-2-Schritte haben die Bedingung phase === 'montage'

      // P2: Außen-Montageorte – nur wenn:
      //   1. Phase 2 aktiv UND
      //   2. in Phase 1 "Außen" als Bereich gewählt wurde
      //   (Türklingel-only-Nutzer überspringen diese Frage komplett)
      'p2_orte_aussen': (s) => s.phase === 'montage' && _has(s.q2_bereiche, 'aussen'),

      // P2: Innen-Montageorte – nur wenn:
      //   1. Phase 2 aktiv UND
      //   2. in Phase 1 "Innen" als Bereich gewählt wurde
      'p2_orte_innen': (s) => s.phase === 'montage' && _has(s.q2_bereiche, 'innen'),

      // P2-Q2: Netzwerk/WLAN verfügbar?
      'p2_netzwerk': (s) => s.phase === 'montage',

      // Postleitzahl
      'p2_postleitzahl': (s) => s.phase === 'montage',

      // Kontaktformular
      'kontakt': (s) => s.phase === 'montage',
    };

    // ════════════════════════════════════════════════════════════════════════
    // 4. SCHRITT-REIHENFOLGE
    //    Legt die logische Abfolge der Schritte fest, UNABHÄNGIG davon,
    //    in welcher Reihenfolge die Blöcke im Editor stehen.
    //    So kann ein Redakteur Blöcke im Editor verschieben, ohne die
    //    Wizard-Logik zu brechen.
    //
    //    Blöcke vom Typ 'product_entry' tauchen hier NICHT auf – sie sind
    //    nur Datenspeicher, keine sichtbaren Schritte.
    // ════════════════════════════════════════════════════════════════════════
    const STEP_ORDER = [
      // Phase 1
      'q1_immobilie',
      'q2_bereiche',
      'q3_steckdose',
      'q4_klingeldraht',
      'q5_groesse',
      'q6_aufzeichnung',
      'q7_speicher',
      'empfehlung',
      // Phase 2
      'p2_orte_aussen',
      'p2_orte_innen',
      'p2_netzwerk',
      'p2_postleitzahl',
      'kontakt',
    ];

    // ════════════════════════════════════════════════════════════════════════
    // 5. EMPFEHLUNGS-ENGINE
    //    ──────────────────
    //    Liest alle Antworten aus und gibt zurück, welche Produkt-Handles
    //    empfohlen werden sollen. Diese Handles entsprechen dem Produkt-Handle-
    //    Feld in den Produkt-Datensatz-Blöcken im Editor.
    //
    //    KAMERA-LOGIK:
    //      Kein Strom → Apollo 2 (Batterie-Kamera)
    //      Strom + kurze Reichweite (≤20m) → Iris Dome
    //      Strom + große Reichweite (>20m) → Oculus Pro
    //      SONDERFALL: Nur Innen ausgewählt + Strom → Q5 wurde übersprungen
    //        → wir empfehlen Iris Dome als vernünftigen Standard für Innen
    //
    //    TÜRKLINGEL-LOGIK:
    //      Eingangsbereich + keine Klingeldrähte → ProV1 (WLAN-Türklingel)
    //      Eingangsbereich + Klingeldrähte vorhanden → Cardea 3 (nutzt Drähte)
    //
    //    SPEICHER-LOGIK:
    //      Aufzeichnung + Netzwerkspeicher gewählt → Chronos NVR
    //      Aufzeichnung + SD-Karte gewählt → SD-Karte(n)
    //      Aufzeichnung + Q7 übersprungen (kein Strom) → SD-Karte (Batterie-Kamera
    //        hat eingebauten SD-Slot)
    // ════════════════════════════════════════════════════════════════════════
    function getRecommendedProducts(s) {
      const products = []; // Hier sammeln wir die empfohlenen Handles

      const bereiche  = s.q2_bereiche || [];
      const hasInnen  = _has(bereiche, 'innen');
      const hasAussen = _has(bereiche, 'aussen');
      const hasEing   = _has(bereiche, 'eingangsbereich');

      // ── Kamera-Empfehlung (für Innen/Außen) ──────────────────────────────
      if (hasInnen || hasAussen) {
        if (s.q3_steckdose === 'nein') {
          // Kein Strom am Montageort → Batterie-Kamera
          products.push('apollo-2');

        } else if (s.q3_steckdose === 'ja') {
          // Strom vorhanden. Jetzt kommt es auf die Größe des Außenbereichs an.
          const q5Shown = hasAussen; // Q5 erscheint nur bei Außen+Strom
          if (!q5Shown || s.q5_groesse === '20m-oder-weniger') {
            // Kurze Reichweite ODER nur Innenbereich → Dome-Kamera
            // (⚠ Annahme: Für reinen Innenbereich ist Iris Dome Standard)
            products.push('iris-dome');
          } else if (s.q5_groesse === '20m-oder-mehr') {
            // Große Reichweite nötig → PTZ/Weitwinkel-Kamera
            products.push('oculus-pro');
          }
        }
      }

      // ── Türklingel-Empfehlung (für Eingangsbereich) ──────────────────────
      if (hasEing) {
        if (s.q4_klingeldraht === 'nein') {
          // Keine vorhandenen Drähte → WLAN-Türklingel
          products.push('prov1');
        } else if (s.q4_klingeldraht === 'ja') {
          // Bestehende Klingeldrähte vorhanden → Modell das diese nutzt
          products.push('cardea-3');
        }
      }

      // ── Speicher-Empfehlung ───────────────────────────────────────────────
      if (s.q6_aufzeichnung === 'ja') {
        const q7WasShown = s.q3_steckdose === 'ja'; // Q7 erscheint nur bei Strom+Aufzeichnung

        if (q7WasShown && s.q7_speicher === 'netzwerk') {
          // Zentraler Netzwerkspeicher (NVR)
          products.push('chronos-nvr');
        } else if (q7WasShown && s.q7_speicher === 'sd-karte') {
          // SD-Karte pro Gerät (Q7 wurde gezeigt, SD gewählt)
          products.push('sd-karte');
        } else if (!q7WasShown) {
          // Q7 wurde übersprungen (kein Strom) → Batterie-Kamera hat SD-Slot
          products.push('sd-karte');
        }
      }

      return products;
    }

    // ════════════════════════════════════════════════════════════════════════
    // 6. LOCALSTORAGE HELFER
    //    ────────────────────
    //    Der Fortschritt des Wizards wird bei jeder Antwort automatisch
    //    gespeichert. So gehen keine Daten verloren, wenn der Browser
    //    refresht wird. Der Schlüssel enthält die Section-ID damit mehrere
    //    Wizard-Instanzen auf einer Seite sich nicht gegenseitig überschreiben.
    // ════════════════════════════════════════════════════════════════════════
    const STORAGE_KEY = 'wp_advisor_' + config.sectionId;

    function saveState(state) {
      // Wir versuchen zu speichern. Wenn localStorage nicht verfügbar ist
      // (z.B. Safari im privaten Modus), arbeiten wir einfach nur im RAM weiter.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // localStorage nicht verfügbar – kein Problem, Wizard funktioniert trotzdem
      }
    }

    function loadState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    function clearState() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }
    }

    // ════════════════════════════════════════════════════════════════════════
    // LESBARE BEZEICHNUNGEN
    //    Wandelt interne Schlüssel (z.B. 'aussen') in lesbares Deutsch um.
    //    Diese Texte erscheinen in der E-Mail an das Verkaufsteam.
    // ════════════════════════════════════════════════════════════════════════
    const LABEL_MAP = {
      // Immobilientypen
      wohnung: 'Wohnung', haus: 'Haus', gewerbe: 'Gewerbe', buero: 'Büro',
      // Bereiche Phase 1
      innen: 'Innen', aussen: 'Außen', eingangsbereich: 'Eingangsbereich',
      // Ja / Nein / Weiß nicht
      ja: 'Ja', nein: 'Nein', 'weiss-nicht': 'Weiß nicht',
      // Bereichsgröße
      '20m-oder-weniger': '20 m oder weniger',
      '20m-oder-mehr':    'Mehr als 20 m',
      // Speicher
      netzwerk:   'Zentraler Netzwerkspeicher (NVR)',
      'sd-karte': 'SD-Karte pro Gerät',
      // Phase-2-Montageorte (außen)
      garage: 'Garage', garten: 'Garten', terrasse: 'Terrasse',
      kellereingang: 'Kellereingang', einfahrt: 'Einfahrt',
      // Phase-2-Montageorte (innen)
      wohnzimmer: 'Wohnzimmer', flur: 'Flur / Diele', keller: 'Keller',
      arbeitszimmer: 'Büro / Arbeitszimmer', lagerraum: 'Lagerraum',
    };

    const PRODUCT_LABELS = {
      'apollo-2':   'Apollo 2 (Batterie-Kamera)',
      'iris-dome':  'Iris Dome Kamera',
      'oculus-pro': 'Oculus Pro Kamera',
      'prov1':      'ProV1 Videotürklingel',
      'cardea-3':   'Cardea 3 Videotürklingel',
      'chronos-nvr':'Chronos NVR (Netzwerkspeicher)',
      'sd-karte':   'SD-Karte(n)',
    };

    // Hilfsfunktion: einzelnen Wert übersetzen
    function _label(val) {
      if (!val) return '—';
      return LABEL_MAP[val] || PRODUCT_LABELS[val] || val;
    }

    // Hilfsfunktion: Array von Werten übersetzen und zusammenfügen
    function _labelList(arr) {
      if (!Array.isArray(arr) || arr.length === 0) return '—';
      return arr.map(_label).join(', ');
    }

    // Prüft ob ein Array einen Wert enthält
    function _has(arr, value) {
      return Array.isArray(arr) && arr.includes(value);
    }

    // Prüft ob ein Array irgendeinen der angegebenen Werte enthält
    function _hasAny(arr, values) {
      return Array.isArray(arr) && values.some(v => arr.includes(v));
    }

    // Formatiert einen Eurobetrag als "1.234,56 €"
    function _formatEur(amount) {
      if (typeof amount !== 'number') return '—';
      return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    }

    // ════════════════════════════════════════════════════════════════════════
    // 7. ADVISOR WIZARD KLASSE
    //    ──────────────────────
    //    Das Herzstück des Wizards. Verwaltet:
    //    - den aktuellen Schritt
    //    - alle Antworten (state)
    //    - das Vor- und Zurücknavigieren
    //    - die Preis-Kalkulation
    //    - das Befüllen des Kontaktformulars
    // ════════════════════════════════════════════════════════════════════════
    class AdvisorWizard {

      constructor(rootEl) {
        this.root = rootEl;
        const sid = config.sectionId;

        // DOM-Elemente, die wir häufig brauchen
        this.progressEl   = rootEl.querySelector('#advisor-progress-' + sid);
        this.progressTrack= rootEl.querySelector('#advisor-progress-track-' + sid);
        this.navEl        = rootEl.querySelector('#advisor-nav-' + sid);
        this.backBtn      = rootEl.querySelector('#advisor-back-' + sid);
        this.nextBtn      = rootEl.querySelector('#advisor-next-' + sid);
        this.successEl    = rootEl.querySelector('#advisor-success-' + sid);
        this.phaseLabelEl = rootEl.querySelector('#advisor-phase-label-' + sid);
        this.stepCountEl  = rootEl.querySelector('#advisor-step-counter-' + sid);
        this.recoContainer= rootEl.querySelector('#advisor-reco-' + sid);

        // Mobile overlay elements
        this.wrapEl     = document.getElementById('advisor-wrap-' + sid);
        this.backdropEl = document.getElementById('advisor-backdrop-' + sid);
        this.triggerEl  = document.getElementById('advisor-trigger-' + sid);
        this.closeBtn   = document.getElementById('advisor-close-' + sid);

        // Alle Schritt-Elemente aus dem DOM holen (kein product_entry!)
        this.allStepEls = Array.from(
          rootEl.querySelectorAll('[data-step-id][data-block-type]:not(template)')
        );

        // STATE: Alle Antworten + Meta-Infos werden hier gespeichert.
        // Ein leeres state-Objekt – wird durch _initFromStorage befüllt
        // wenn localStorage Daten hat.
        this.state = this._freshState();

        this._bindEvents();

        // Hat der Nutzer soeben das Formular abgeschickt?
        // Shopify hängt nach erfolgreicher Kontaktformular-Übertragung
        // ?contact_posted=true an die URL – so erkennen wir den Erfolg.
        if (new URLSearchParams(window.location.search).has('contact_posted')) {
          clearState();
          this._showSuccess();

          if (window.innerWidth < 700) {
            // Mobil: Bottom-Sheet öffnen, damit die Erfolgsseite sichtbar ist
            this._openOverlay();
          } else {
            // Desktop: Wizard ist inline auf der Seite – nach dem Reload
            // sanft dorthin scrollen, damit der Nutzer die Bestätigung sieht.
            // requestAnimationFrame wartet bis das Layout fertig gerendert ist.
            requestAnimationFrame(() => {
              this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        } else {
          this._initFromStorage();
        }
      }

      // ── Mobile overlay ─────────────────────────────────────────────────────

      _openOverlay() {
        if (this.wrapEl)     this.wrapEl.classList.add('is-open');
        if (this.backdropEl) this.backdropEl.classList.add('is-open');
        document.body.classList.add('advisor-overlay-open');
        // Scroll the overlay to the top when (re-)opening
        if (this.root) this.root.scrollTop = 0;
      }

      _closeOverlay() {
        if (this.wrapEl)     this.wrapEl.classList.remove('is-open');
        if (this.backdropEl) this.backdropEl.classList.remove('is-open');
        document.body.classList.remove('advisor-overlay-open');
      }

      // ──────────────────────────────────────────────────────────────────────
      // State-Verwaltung
      // ──────────────────────────────────────────────────────────────────────

      /** Erstellt ein sauberes, leeres State-Objekt */
      _freshState() {
        return {
          // Phase-1-Antworten (Schlüssel = step_id)
          q1_immobilie:    null,     // String, z.B. 'wohnung'
          q2_bereiche:     [],       // Array, z.B. ['innen', 'aussen']
          q3_steckdose:    null,
          q4_klingeldraht: null,
          q5_groesse:      null,
          q6_aufzeichnung: null,
          q7_speicher:     null,

          // Ergebnis der Empfehlungs-Engine
          recommendedProducts: [],

          // Phase-Schalter: null = Phase 1, 'montage' = Phase 2
          phase: null,

          // Phase-2-Antworten
          p2_orte_aussen: [],        // Array der Außen-Montageorte (Garage, Garten etc.)
          p2_orte_innen:  [],        // Array der Innen-Montageorte (Wohnzimmer, Flur etc.)
          p2_netzwerk:  null,
          p2_postleitzahl: '',

          // Berechnetes Ergebnis (wird beim Anzeigen des Kontaktformulars befüllt)
          costs: null,
        };
      }

      /** Versucht den letzten Zustand aus localStorage zu laden */
      _initFromStorage() {
        const saved = loadState();
        if (saved) {
          // Gespeicherten Zustand übernehmen (merge mit frischem state für neue Felder)
          this.state = Object.assign(this._freshState(), saved);
        }
        // Zum aktuellen "Schritt" navigieren – wenn gespeichert, zum letzten aktiven
        const firstVisible = this._visibleSteps()[0];
        if (firstVisible) {
          this._goToStep(firstVisible);
          // UI-Zustand wiederherstellen (Buttons markieren etc.)
          this._restoreSelections();
        }
      }

      /** Stellt die UI-Auswahl-Zustände aus dem State wieder her */
      _restoreSelections() {
        // Einzel-/Mehrfachauswahl-Buttons wiederherstellen
        this.allStepEls.forEach(stepEl => {
          const sid = stepEl.dataset.stepId;
          const answer = this.state[sid];
          if (!answer) return;

          stepEl.querySelectorAll('.advisor-choice').forEach(btn => {
            const isSelected = Array.isArray(answer)
              ? answer.includes(btn.dataset.value)
              : answer === btn.dataset.value;
            btn.classList.toggle('is-selected', isSelected);
            btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });

          // Mengenfelder wiederherstellen
          if (typeof answer === 'object' && !Array.isArray(answer)) {
            stepEl.querySelectorAll('.quantity-selector__input').forEach(inp => {
              inp.value = answer[inp.dataset.productHandle] || 0;
            });
          }

          // PLZ wiederherstellen
          const zipInp = stepEl.querySelector('.advisor-zip-input');
          if (zipInp && typeof answer === 'string') zipInp.value = answer;
        });
      }

      // ──────────────────────────────────────────────────────────────────────
      // Navigation
      // ──────────────────────────────────────────────────────────────────────

      /**
       * Gibt die Schritte zurück, die aktuell sichtbar sein sollen.
       * - Sortierung: nach STEP_ORDER (nicht nach DOM-Reihenfolge der Blöcke)
       * - Filterung: nur Schritte, deren FLOW_RULES-Bedingung erfüllt ist
       */
      _visibleSteps() {
        return STEP_ORDER
          .map(id => this.allStepEls.find(el => el.dataset.stepId === id))
          .filter(el => {
            if (!el) return false;
            const rule = FLOW_RULES[el.dataset.stepId];
            return rule ? rule(this.state) : true;
          });
      }

      /** Wechselt zu einem bestimmten Schritt */
      _goToStep(stepEl) {
        if (!stepEl) return;

        // Alle Schritte ausblenden, dann den gewählten einblenden
        this.allStepEls.forEach(s => s.classList.remove('is-active'));
        stepEl.classList.add('is-active');
        this.state.currentStep = stepEl.dataset.stepId;
        saveState(this.state);

        const blockType = stepEl.dataset.blockType;

        // Sonderfunktionen für bestimmte Schritt-Typen
        if (blockType === 'recommendation') this._renderRecommendations(stepEl);
        if (blockType === 'contact_form')   this._calculateAndPopulate();

        this._updateProgress();
        this._updateNav();
        this._updatePhaseLabel();
      }

      _next() {
        const visible = this._visibleSteps();
        const idx = visible.indexOf(this.allStepEls.find(e => e.dataset.stepId === this.state.currentStep));
        if (idx < visible.length - 1) this._goToStep(visible[idx + 1]);
      }

      _back() {
        const visible = this._visibleSteps();
        const idx = visible.indexOf(this.allStepEls.find(e => e.dataset.stepId === this.state.currentStep));
        if (idx > 0) this._goToStep(visible[idx - 1]);
      }

      _reset() {
        clearState();
        this.state = this._freshState();

        // UI aufräumen
        this.root.querySelectorAll('.advisor-choice').forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        this.root.querySelectorAll('.quantity-selector__input').forEach(i => { i.value = 0; });
        this.root.querySelectorAll('.advisor-zip-input').forEach(i => { i.value = ''; });

        this.successEl.classList.remove('is-active');
        this.navEl.style.display = '';

        // ?contact_posted=true aus der URL entfernen (ohne Seitenneulad)
        const url = new URL(window.location);
        url.searchParams.delete('contact_posted');
        history.replaceState({}, '', url.toString());

        this._goToStep(this._visibleSteps()[0]);
      }

      _showSuccess() {
        this.allStepEls.forEach(s => s.classList.remove('is-active'));
        if (this.navEl) this.navEl.style.display = 'none';
        if (this.successEl) this.successEl.classList.add('is-active');
        if (this.progressEl) this.progressEl.style.width = '100%';
        if (this.progressTrack) this.progressTrack.setAttribute('aria-valuenow', '100');
      }

      // ──────────────────────────────────────────────────────────────────────
      // Antworten aufzeichnen (State-Mutationen)
      // ──────────────────────────────────────────────────────────────────────

      _recordSingle(stepId, value) {
        this.state[stepId] = value;
        saveState(this.state);
      }

      _recordMulti(stepId, value) {
        if (!Array.isArray(this.state[stepId])) this.state[stepId] = [];
        const arr = this.state[stepId];
        const idx = arr.indexOf(value);
        if (idx > -1) arr.splice(idx, 1); else arr.push(value);
        saveState(this.state);
      }

      _recordQuantities(stepEl) {
        const sid = stepEl.dataset.stepId;
        const qtys = {};
        stepEl.querySelectorAll('.quantity-selector__input').forEach(inp => {
          const n = parseInt(inp.value, 10) || 0;
          if (n > 0) qtys[inp.dataset.productHandle] = n;
        });
        this.state[sid] = qtys;
        saveState(this.state);
      }

      _recordZip(stepEl) {
        const sid = stepEl.dataset.stepId;
        const val = stepEl.querySelector('.advisor-zip-input')?.value.trim() || '';
        this.state[sid] = val;
        // Für das Kontaktformular auch in p2_postleitzahl ablegen
        this.state.p2_postleitzahl = val;
        saveState(this.state);
      }

      // ──────────────────────────────────────────────────────────────────────
      // Validierung
      // ──────────────────────────────────────────────────────────────────────

      /**
       * Prüft ob der aktuelle Schritt vollständig ausgefüllt wurde.
       * Gibt true zurück wenn "Weiter" freigegeben werden kann.
       */
      _isValid(stepEl) {
        if (!stepEl) return false;
        const type  = stepEl.dataset.inputType;
        const btype = stepEl.dataset.blockType;

        // Empfehlung und Kontaktformular haben eigene Validierung
        if (btype === 'recommendation') return true;
        if (btype === 'contact_form')   return true; // Browser-Validierung reicht

        switch (type) {
          case 'single_choice':
            return !!stepEl.querySelector('.advisor-choice.is-selected');

          case 'multi_choice':
            // Mindestens eine Option muss gewählt sein
            return !!stepEl.querySelector('.advisor-choice.is-selected');

          case 'quantity_input': {
            // Mindestens ein Gerät muss eingegeben sein
            const total = Array.from(stepEl.querySelectorAll('.quantity-selector__input'))
              .reduce((sum, i) => sum + (parseInt(i.value, 10) || 0), 0);
            return total > 0;
          }

          case 'zip_input': {
            const zip = stepEl.querySelector('.advisor-zip-input')?.value.trim() || '';
            return /^\d{5}$/.test(zip); // Genau 5 Ziffern
          }

          default: return true;
        }
      }

      // ──────────────────────────────────────────────────────────────────────
      // Produktempfehlungen rendern
      // ──────────────────────────────────────────────────────────────────────

      _renderRecommendations() {
        const container = this.recoContainer;
        if (!container) return;

        // Produkt-Handles vom Empfehlungs-Engine holen
        const handles = getRecommendedProducts(this.state);
        this.state.recommendedProducts = handles;
        saveState(this.state);

        container.innerHTML = '';

        if (handles.length === 0) {
          container.innerHTML = '<p class="text-subdued">Keine passenden Produkte gefunden. Bitte kontaktieren Sie uns direkt.</p>';
          return;
        }

        handles.forEach(handle => {
          // Sucht das passende <template data-product-handle="..."> Element
          const tpl = this.root.querySelector(`template[data-product-handle="${handle}"]`);
          if (tpl) {
            // Klont den Template-Inhalt und fügt ihn in den Container ein
            container.appendChild(tpl.content.cloneNode(true));
          } else {
            // Kein Block konfiguriert → Hinweis für den Entwickler
            const fallback = document.createElement('div');
            fallback.className = 'advisor-product-card';
            fallback.innerHTML = `<p style="color:var(--advisor-accent);font-size:var(--text-xs)">
              Produkt "${handle}" – kein Produkt-Datensatz-Block konfiguriert.
            </p>`;
            container.appendChild(fallback);
          }
        });
      }

      // ──────────────────────────────────────────────────────────────────────
      // Preis-Kalkulation (Phase 2)
      // ──────────────────────────────────────────────────────────────────────

      /**
       * Berechnet den Schätzpreis für Phase 2 (Montage-Angebot).
       *
       * FORMEL:
       * ├── Gerätekosten:
       * │   ├── Empfohlene Kamera × Anzahl Phase-2-Bereiche
       * │   ├── Türklingel × 1 (falls empfohlen)
       * │   ├── Chronos NVR × 1 (falls empfohlen)
       * │   └── SD-Karte × Anzahl Kameras (falls empfohlen)
       * ├── Montagekosten:
       * │   ├── 1 Gerät: Pauschale (z.B. 500 €)
       * │   ├── 2 Geräte: Satz pro Gerät (z.B. 150 €/Stk.)
       * │   └── 3+ Geräte: reduzierter Satz (z.B. 100 €/Stk.)
       * └── Anschlusspauschale: pro installiertem Gerät (ohne SD-Karten)
       */
      _calculateCosts() {
        const recommended = this.state.recommendedProducts || [];
        const orteAussen  = this.state.p2_orte_aussen || [];
        const orteInnen   = this.state.p2_orte_innen  || [];

        // Kamera-Anzahl = Summe aller gewählten Montageorte (außen + innen).
        // Jeder Standort braucht eine Kamera.
        // Türklingel-only-Nutzer haben beide Fragen übersprungen → 0 Kameras.
        const cameraCount = orteAussen.length + orteInnen.length;

        // Welche Kamera wurde empfohlen?
        const cameraHandle = recommended.find(h => PRICE_CONFIG.cameraHandles.includes(h));
        const cameraPrice  = cameraHandle ? (PRODUCT_CATALOG[cameraHandle]?.priceEur || 0) : 0;

        // Türklingel (immer 1 Stück)
        const doorbellHandle = recommended.find(h => PRICE_CONFIG.doorbellHandles.includes(h));
        const doorbellPrice  = doorbellHandle ? (PRODUCT_CATALOG[doorbellHandle]?.priceEur || 0) : 0;
        const doorbellCount  = doorbellHandle ? 1 : 0;

        // NVR (immer 1 Stück)
        const hasNVR  = recommended.includes('chronos-nvr');
        const nvrPrice= hasNVR ? (PRODUCT_CATALOG['chronos-nvr']?.priceEur || 0) : 0;
        const nvrCount= hasNVR ? 1 : 0;

        // SD-Karten (eine pro Kamera-Standort)
        const hasSD      = recommended.includes('sd-karte');
        const sdPrice    = hasSD ? (PRODUCT_CATALOG['sd-karte']?.priceEur || 0) : 0;
        const sdCount    = hasSD ? cameraCount : 0;

        // Gerätekosten summieren
        const geraetekosten =
          cameraPrice  * cameraCount  +
          doorbellPrice* doorbellCount+
          nvrPrice     * nvrCount     +
          sdPrice      * sdCount;

        // Anzahl "installierter" Geräte (SD-Karten werden nicht montiert)
        const installableDevices = cameraCount + doorbellCount + nvrCount;

        // Montagekosten nach Staffel berechnen
        const tier = PRICE_CONFIG.montage[installableDevices] || PRICE_CONFIG.montage.default;
        const montagekosten = tier.mode === 'flat'
          ? tier.rate
          : tier.rate * installableDevices;

        // Anschlusspauschale pro installiertem Gerät
        const pauschale = PRICE_CONFIG.anschlusspauschale * installableDevices;

        const total = geraetekosten + montagekosten + pauschale;

        return {
          geraetekosten, montagekosten, pauschale, total,
          cameraCount, doorbellCount, nvrCount, sdCount,
          installableDevices,
          cameraHandle, doorbellHandle,
        };
      }

      /**
       * Berechnet den Preis und befüllt:
       * 1. Die sichtbare Kosten-Tabelle auf der Seite
       * 2. Die versteckten Formular-Felder (die mit der E-Mail verschickt werden)
       */
      _calculateAndPopulate() {
        const sid  = config.sectionId;
        const costs = this._calculateCosts();
        this.state.costs = costs;
        saveState(this.state);

        // ── Sichtbare Kosten-Tabelle befüllen ──
        const setText = (id, val) => {
          const el = this.root.querySelector('#' + id);
          if (el) el.textContent = val;
        };

        setText(`advisor-display-geraete-${sid}`,   _formatEur(costs.geraetekosten));
        setText(`advisor-display-montage-${sid}`,   _formatEur(costs.montagekosten));
        setText(`advisor-display-pauschale-${sid}`, _formatEur(costs.pauschale));
        setText(`advisor-display-total-${sid}`,     _formatEur(costs.total));

        // ── Versteckte Formular-Felder befüllen ──
        // Diese Werte landen in der E-Mail-Anfrage, die der Kunde abschickt.
        const setHidden = (id, val) => {
          const el = this.root.querySelector(`#advisor-h-${id}-${sid}`);
          if (el) el.value = val;
        };

        const s = this.state;
        setHidden('immobilie',    _label(s.q1_immobilie));
        setHidden('bereiche1',    _labelList(s.q2_bereiche));
        setHidden('steckdose',    _label(s.q3_steckdose));
        setHidden('aufzeichnung', _label(s.q6_aufzeichnung));
        setHidden('speicher',     s.q7_speicher ? _label(s.q7_speicher) : (s.q6_aufzeichnung === 'ja' ? 'SD-Karte (automatisch, kein Strom am Montageort)' : '—'));
        setHidden('orte-aussen', _labelList(s.p2_orte_aussen));
        setHidden('orte-innen',  _labelList(s.p2_orte_innen));
        setHidden('netzwerk',     _label(s.p2_netzwerk));
        setHidden('plz',          s.p2_postleitzahl || '—');
        setHidden('produkte',     (s.recommendedProducts || []).map(h => PRODUCT_LABELS[h] || h).join(', ') || '—');
        setHidden('geraete',      _formatEur(costs.geraetekosten));
        setHidden('montage',      _formatEur(costs.montagekosten));
        setHidden('pauschale',    _formatEur(costs.pauschale));
        setHidden('total',        _formatEur(costs.total));
        setHidden('anzahl',       `${costs.installableDevices} Gerät(e): ${costs.cameraCount}× Kamera, ${costs.doorbellCount}× Türklingel, ${costs.nvrCount}× NVR`);
      }

      // ──────────────────────────────────────────────────────────────────────
      // UI-Updates
      // ──────────────────────────────────────────────────────────────────────

      _updateProgress() {
        const visible = this._visibleSteps();
        const currentEl = this.allStepEls.find(e => e.dataset.stepId === this.state.currentStep);
        const idx = visible.indexOf(currentEl);
        const pct = visible.length > 0 ? Math.round(((idx + 1) / visible.length) * 100) : 0;

        if (this.progressEl) this.progressEl.style.width = pct + '%';
        if (this.progressTrack) this.progressTrack.setAttribute('aria-valuenow', pct);
      }

      _updateNav() {
        const visible = this._visibleSteps();
        const currentEl = this.allStepEls.find(e => e.dataset.stepId === this.state.currentStep);
        const idx = visible.indexOf(currentEl);
        const btype = currentEl?.dataset.blockType;

        // Zurück-Button: immer sichtbar, aber am ersten Schritt deaktiviert
        this.backBtn.disabled = idx <= 0;

        if (btype === 'contact_form') {
          // Das Formular hat seinen eigenen Absenden-Button → Weiter verstecken
          this.nextBtn.style.display = 'none';
        } else if (btype === 'recommendation') {
          // Phase 1 endet hier → Weiter-Button nicht zeigen
          // Der Nutzer kann "Einbau anfragen" klicken oder einfach Produkte ansehen
          this.nextBtn.style.display = 'none';
        } else {
          this.nextBtn.style.display = '';
          this.nextBtn.disabled      = !this._isValid(currentEl);
          this.nextBtn.textContent   = 'Weiter →';
        }
      }

      _updatePhaseLabel() {
        if (!this.phaseLabelEl) return;

        // Determine phase from which step we're currently on,
        // not from state.phase — so going backwards shows the right label.
        const phase2Steps = ['p2_orte_aussen', 'p2_orte_innen', 'p2_netzwerk', 'p2_postleitzahl', 'kontakt'];
        const isPhase2 = phase2Steps.includes(this.state.currentStep);

        // On mobile the badge needs to be short so everything fits on one line
        // alongside the step counter and reset link.
        const isMobile = window.innerWidth < 700;
        if (isMobile) {
          this.phaseLabelEl.textContent = isPhase2 ? 'Phase 2' : 'Phase 1';
        } else {
          this.phaseLabelEl.textContent = isPhase2 ? 'Phase 2: Montage-Angebot' : 'Phase 1: Produktempfehlung';
        }

        // Schritt-Zähler
        const visible = this._visibleSteps();
        const currentEl = this.allStepEls.find(e => e.dataset.stepId === this.state.currentStep);
        const idx = visible.indexOf(currentEl);
        if (this.stepCountEl && idx >= 0) {
          this.stepCountEl.textContent = `Schritt ${idx + 1} von ${visible.length}`;
        }
      }

      // ──────────────────────────────────────────────────────────────────────
      // Event-Handler
      // ──────────────────────────────────────────────────────────────────────

      _bindEvents() {
        // ── Mobile overlay triggers ──────────────────────────────────────────
        if (this.triggerEl) {
          this.triggerEl.addEventListener('click', () => this._openOverlay());
        }
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', () => this._closeOverlay());
        }
        if (this.backdropEl) {
          this.backdropEl.addEventListener('click', () => this._closeOverlay());
        }

        // Ein einziger Event-Listener auf dem Root-Element (Event Delegation).
        // Besser als viele einzelne Listener – funktioniert auch für dynamisch
        // eingefügte Elemente.
        this.root.addEventListener('click', e => {
          // ── Auswahl-Buttons ──────────────────────────────────────────────
          const choiceBtn = e.target.closest('.advisor-choice');
          if (choiceBtn) {
            const stepEl = choiceBtn.closest('[data-step-id]');
            const type   = stepEl?.dataset.inputType;
            const value  = choiceBtn.dataset.value;

            if (type === 'single_choice') {
              // Alle anderen abwählen, nur diesen wählen
              stepEl.querySelectorAll('.advisor-choice').forEach(b => {
                b.classList.remove('is-selected');
                b.setAttribute('aria-pressed', 'false');
              });
              choiceBtn.classList.add('is-selected');
              choiceBtn.setAttribute('aria-pressed', 'true');
              this._recordSingle(stepEl.dataset.stepId, value);
              this._updateNav();

            } else if (type === 'multi_choice') {
              // Toggle: auswählen oder abwählen
              const isNowSelected = choiceBtn.classList.toggle('is-selected');
              choiceBtn.setAttribute('aria-pressed', isNowSelected ? 'true' : 'false');
              this._recordMulti(stepEl.dataset.stepId, value);
              this._updateNav();
            }
            return;
          }

          // ── Mengen +/- Buttons ───────────────────────────────────────────
          const qBtn = e.target.closest('.quantity-selector__button');
          if (qBtn) {
            const dir   = parseInt(qBtn.dataset.dir, 10);
            const inp   = qBtn.closest('.quantity-selector')?.querySelector('.quantity-selector__input');
            if (inp) {
              inp.value = Math.max(0, Math.min(99, (parseInt(inp.value, 10) || 0) + dir));
              const stepEl = inp.closest('[data-step-id]');
              this._recordQuantities(stepEl);
              this._updateNav();
            }
            return;
          }

          // ── "Einbau anfragen" (Phase 1 → Phase 2) ───────────────────────
          if (e.target.closest('[data-action="start-montage"]')) {
            // Setzt den "Phase-Schalter" auf 'montage'.
            // Dadurch werden in _visibleSteps() die Phase-2-Schritte sichtbar.
            this.state.phase = 'montage';
            saveState(this.state);
            this._next();
            return;
          }

          // ── Navigations-Buttons ──────────────────────────────────────────
          if (e.target === this.nextBtn) { this._next(); return; }
          if (e.target === this.backBtn) { this._back(); return; }

          // ── Reset-Button (auf der Erfolgsseite und im Meta-Bereich) ────────
          if (e.target.closest('[data-action="reset"]')) { this._closeOverlay(); this._reset(); return; }
        });

        // PLZ-Eingabe: live validieren während Nutzer tippt
        this.root.addEventListener('input', e => {
          // PLZ-Feld
          const zipInp = e.target.closest('.advisor-zip-input');
          if (zipInp) {
            // Nur Ziffern erlauben, max 5 Stellen
            zipInp.value = zipInp.value.replace(/\D/g, '').slice(0, 5);
            const stepEl = zipInp.closest('[data-step-id]');
            this._recordZip(stepEl);
            this._updateNav();
          }

          // Mengen-Textfeld (direkte Eingabe)
          const qInp = e.target.closest('.quantity-selector__input');
          if (qInp) {
            const stepEl = qInp.closest('[data-step-id]');
            this._recordQuantities(stepEl);
            this._updateNav();
          }
        });

        // Kurz vor dem Absenden: Felder aktualisieren + Backend benachrichtigen
        const form = this.root.querySelector('form');
        if (form) {
          form.addEventListener('submit', () => {
            this._calculateAndPopulate();
            this._notifyBackend(form);
          });
        }
      }

      // Schickt eine gebrandete Bestätigungsmail an den Kunden über das eigene Backend.
      // Läuft parallel zum nativen Shopify-Submit; text/plain vermeidet einen
      // CORS-Preflight, keepalive lässt die Anfrage die Seitennavigation überleben.
      _notifyBackend(form) {
        var ENDPOINT = config.endpoint;
        if (!ENDPOINT) return;
        var val = function (name) {
          var el = form.querySelector('[name="' + name + '"]');
          return (el && el.value ? el.value : '').trim();
        };
        try {
          var payload = {
            form_type:     'configurator',
            name:          val('contact[name]'),
            email:         val('contact[email]'),
            phone:         val('contact[phone]'),
            immobilie:     val('contact[Immobilientyp]'),
            bereiche:      val('contact[Überwachungsbereiche]'),
            produkte:      val('contact[Empfohlene Produkte]'),
            zipcode:       val('contact[Postleitzahl]'),
            geraetekosten: val('contact[Gerätekosten]'),
            montagekosten: val('contact[Montagekosten]'),
            pauschale:     val('contact[Anschlusspauschale]'),
            summe:         val('contact[Gesamtpreis (Schätzung)]'),
            hp:            val('hp')
          };
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(payload),
            keepalive: true
          }).catch(function () {});
        } catch (e) { /* Bestätigungsmail ist optional */ }
      }
    }


    // ════════════════════════════════════════════════════════════════════════
    // INITIALISIERUNG
    // ════════════════════════════════════════════════════════════════════════
    const root = document.getElementById('advisor-' + config.sectionId);
    if (root && !root.hasAttribute('data-advisor-ready')) {
      root.setAttribute('data-advisor-ready', '');
      new AdvisorWizard(root);
    }
  }

  function initAll() {
    document.querySelectorAll('script[data-install-advisor-config]').forEach(function (element) {
      try {
        setupAdvisor(JSON.parse(element.textContent));
      } catch (error) {
        console.error('install-advisor:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
